import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lock, Building2, Users, UserPlus, X, Loader2, Search } from "lucide-react";
import type { 
  PersonaVisibility, 
  PersonaPermission,
  OrgUser,
  PersonaAcl,
  LibraryPersona,
} from "@/lib/persona-types";
import { cn } from "@/lib/utils";

interface PersonaShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: LibraryPersona | null;
  currentAcl: PersonaAcl[];
  orgUsers: OrgUser[];
  onSave: (visibility: PersonaVisibility, userPermissions: Array<{ user_id: string; permission: PersonaPermission }>) => Promise<void>;
  isLoading?: boolean;
}

export function PersonaShareModal({
  open,
  onOpenChange,
  persona,
  currentAcl,
  orgUsers,
  onSave,
  isLoading,
}: PersonaShareModalProps) {
  const [visibility, setVisibility] = useState<PersonaVisibility>("private");
  const [userPermissions, setUserPermissions] = useState<Array<{ user_id: string; permission: PersonaPermission; email?: string; name?: string }>>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserPicker, setShowUserPicker] = useState(false);

  useEffect(() => {
    if (persona && open) {
      setVisibility(persona.visibility);
      setUserPermissions(
        currentAcl.map(acl => ({
          user_id: acl.user_id,
          permission: acl.permission,
          email: acl.user_email,
          name: acl.user_name || undefined,
        }))
      );
    }
  }, [persona, currentAcl, open]);

  const handleAddUser = (user: OrgUser) => {
    if (!userPermissions.find(p => p.user_id === user.id)) {
      setUserPermissions([
        ...userPermissions,
        { user_id: user.id, permission: "viewer", email: user.email, name: user.full_name || undefined },
      ]);
    }
    setSearchQuery("");
    setShowUserPicker(false);
  };

  const handleRemoveUser = (userId: string) => {
    setUserPermissions(userPermissions.filter(p => p.user_id !== userId));
  };

  const handlePermissionChange = (userId: string, permission: PersonaPermission) => {
    setUserPermissions(userPermissions.map(p => 
      p.user_id === userId ? { ...p, permission } : p
    ));
  };

  const handleSubmit = async () => {
    const finalVisibility = userPermissions.length > 0 && visibility === "custom" ? "custom" : visibility;
    await onSave(
      finalVisibility,
      userPermissions.map(p => ({ user_id: p.user_id, permission: p.permission }))
    );
    onOpenChange(false);
  };

  const filteredUsers = orgUsers.filter(u => 
    !userPermissions.find(p => p.user_id === u.id) &&
    (u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
     u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Persona</DialogTitle>
          <DialogDescription>
            Control who can view and edit "{persona?.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Visibility Options */}
          <RadioGroup
            value={visibility}
            onValueChange={(v) => setVisibility(v as PersonaVisibility)}
            className="space-y-3"
          >
            <label
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                visibility === "private" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/50"
              )}
            >
              <RadioGroupItem value="private" id="private" />
              <Lock className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">Private</p>
                <p className="text-xs text-muted-foreground">Only you can access</p>
              </div>
            </label>

            <label
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                visibility === "org" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/50"
              )}
            >
              <RadioGroupItem value="org" id="org" />
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">Organization</p>
                <p className="text-xs text-muted-foreground">Everyone in your org can view</p>
              </div>
            </label>

            <label
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                visibility === "custom" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/50"
              )}
            >
              <RadioGroupItem value="custom" id="custom" />
              <Users className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">Specific People</p>
                <p className="text-xs text-muted-foreground">Choose who can access</p>
              </div>
            </label>
          </RadioGroup>

          {/* User Picker (only for custom visibility) */}
          {visibility === "custom" && (
            <div className="space-y-3 pt-2 border-t border-border">
              <Label>Share with specific people</Label>
              
              {/* Search/Add User */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowUserPicker(true);
                  }}
                  onFocus={() => setShowUserPicker(true)}
                  className="pl-9"
                />
                
                {/* User Dropdown */}
                {showUserPicker && searchQuery && filteredUsers.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.slice(0, 5).map(user => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleAddUser(user)}
                        className="w-full flex items-center gap-3 p-2 hover:bg-muted/50 text-left"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {user.full_name?.[0] || user.email[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {user.full_name || user.email}
                          </p>
                          {user.full_name && (
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          )}
                        </div>
                        <UserPlus className="w-4 h-4 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Users List */}
              {userPermissions.length > 0 && (
                <div className="space-y-2">
                  {userPermissions.map(up => (
                    <div
                      key={up.user_id}
                      className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {up.name?.[0] || up.email?.[0]?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {up.name || up.email}
                        </p>
                      </div>
                      <Select
                        value={up.permission}
                        onValueChange={(v) => handlePermissionChange(up.user_id, v as PersonaPermission)}
                      >
                        <SelectTrigger className="w-24 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleRemoveUser(up.user_id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {userPermissions.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No users added yet. Search to add people.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
